import React from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material'
import DownloadSettlementButton from '../../components/action_button/DownloadSettlementButton'
import SettlementApprovalButton from '../../components/action_button/SettlementApprovalButton'
import {
  UI_COLOR_ACCENT_ORANGE,
  UI_COLOR_ACCENT_ORANGE_ALT_HOVER
} from '../../constants/uiColors'
import { APPROVAL_STATUS } from '../helpers/detailsConstants'
import {
  getApprovalStatusUI,
  isDepositBalanced,
  resolveApprovalStatus
} from '../helpers/detailsHelpers'

const DIALOG_PRIMARY_BUTTON_SX = {
  backgroundColor: UI_COLOR_ACCENT_ORANGE,
  '&:hover': {
    backgroundColor: UI_COLOR_ACCENT_ORANGE_ALT_HOVER
  }
}

const DIALOG_CANCEL_BUTTON_SX = {
  color: '#757575',
  '&:hover': {
    backgroundColor: 'rgba(117, 117, 117, 0.08)'
  }
}

export default function SettlementApprovalActions({
  settlement,
  canWrite,
  canSeeAnnulledDocumentsTab,
  hasUnsavedChanges = false,
  loading,
  onDiscardUnsavedChanges,
  onApprove,
  showStatus = true,
  showDownload = true,
  sx
}) {
  const [confirmAction, setConfirmAction] = React.useState(null)
  const status = resolveApprovalStatus(settlement)
  const balanced = isDepositBalanced(settlement)
  const shouldShowApprove =
    canWrite &&
    ![APPROVAL_STATUS.SENT, APPROVAL_STATUS.RECEIVED, APPROVAL_STATUS.INSERTED_ERP].includes(status)
  const disabledApproval = !balanced || loading || hasUnsavedChanges
  const approvalTooltip = hasUnsavedChanges
    ? 'Guarde o cancele la edición del depósito antes de aprobar.'
    : !balanced
      ? 'El total a depositar no cuadra con el total depositado.'
      : ''
  const dialogTitle = 'Aprobar liquidación'
  const dialogText = 'Se registrará la aprobación del usuario actual para esta liquidación.'
  const [pendingAction, setPendingAction] = React.useState(null)

  const handleConfirm = async () => {
    setConfirmAction(null)
    await onApprove()
  }

  const handleApprovalClick = () => {
    const nextAction = 'receive'

    if (hasUnsavedChanges) {
      setPendingAction(nextAction)
      return
    }

    setConfirmAction(nextAction)
  }

  const handleConfirmPendingAction = () => {
    if (!pendingAction) return

    onDiscardUnsavedChanges?.()
    setConfirmAction(pendingAction)
    setPendingAction(null)
  }

  return (
    <>
      <Box
        sx={[
          {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1.5,
            flexWrap: 'wrap',
            mb: showStatus ? 2 : 0
          },
          ...(Array.isArray(sx) ? sx : [sx])
        ]}
      >
        {showStatus && <SettlementApprovalStatus settlement={settlement} />}

        {showDownload && (
          <DownloadSettlementButton
            data={settlement}
            canSeeAnnulledDocumentsTab={canSeeAnnulledDocumentsTab}
            canWrite={canWrite}
          />
        )}

        {shouldShowApprove && (
          <SettlementApprovalButton
            disabled={disabledApproval}
            tooltip={approvalTooltip}
            onClick={handleApprovalClick}
          />
        )}
      </Box>

      <Dialog open={Boolean(pendingAction)} onClose={() => setPendingAction(null)}>
        <DialogTitle>Cambios sin guardar</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Hay cambios sin guardar en el detalle de depósitos. Si continúa, esos cambios se
            perderán. ¿Desea seguir con la aprobación?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingAction(null)} sx={DIALOG_CANCEL_BUTTON_SX}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmPendingAction}
            autoFocus
            sx={DIALOG_PRIMARY_BUTTON_SX}
          >
            Continuar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirmAction)} onClose={() => setConfirmAction(null)}>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>{dialogText}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)} sx={DIALOG_CANCEL_BUTTON_SX}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleConfirm} autoFocus sx={DIALOG_PRIMARY_BUTTON_SX}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export function SettlementApprovalStatus({ settlement, sx }) {
  const statusUI = getApprovalStatusUI(settlement)

  return (
    <div className="status-settlements">
      <b className="order-status-b">Estado:</b>
      <span className={`order-status ${statusUI.className}`}>{statusUI.label}</span>
    </div>
  )
}
